import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button, TextField, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, CircularProgress, Select, FormControl, InputLabel, Snackbar, Alert, Slide } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone'; // 🟢 Synchronisiert
import CloudSyncIcon from '@mui/icons-material/CloudSync'; // 🟡 Bearbeitet


import { useSessionStore } from "./SessionStore"; // Zustand-Store für die aktuelle Sitzung
import PromptButtons from './Promptbuttons';
import PromptButtons2 from './Promptbuttons2';
import SessionDetailsDev from './SessionDetailsDev';
import { useSiteSettings } from './context/SiteSettingsContext';

// CSS-Datei importieren
import './css/App.css';


type ApiCallFunction = () => Promise<void>;


//Beginn der App //

function DevTools() {
  const [selectedAudio, setSelectedAudio] = useState('');
  const [transcriptModel, setTranscriptModel] = useState('');
  const [transcriptContent, setTranscriptContent] = useState('');
  const [myprompt, setPrompt] = useState('');
  const [llmResponse, setLlmResponse] = useState('');
  const [dialogLlmOpen, setDialogLlmOpen] = useState(false); // Dialog-Status
  const [dialogIonosOpen, setDialogIonosOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modelLlm, setModelLlm] = useState('meta-llama/Llama-3.3-70B-Instruct'); // Parameter für das Modell für local
  const [selectedLlm, setSelectedLlm] = useState<string>(''); // Initialwert leer
  const closeLoadingDialog = () => setIsLoading(false);
  const [user, setUser] = useState<string | null>(null);
  const [mypromptCorrection, setPromptCorrection] = useState('');
  const [llmResponseCorrection, setLlmResponseCorrection] = useState('');
  const [dialogIonosCorrectionOpen, setDialogIonosCorrectionOpen] = useState(false);
  const [modelLlmCorrection, setModelLlmCorrection] = useState('meta-llama/Llama-3.3-70B-Instruct');
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const [promptButtonsCorrection, setPromptButtonsCorrection] = useState<string[]>([]);
  const [isPreparingAudioDownload, setIsPreparingAudioDownload] = useState(false);

  //Sitzung
  const [isSessionSelected, setIsSessionSelected] = useState<boolean>(false);
  const sessionData = useSessionStore((state) => state.sessionData);

  const [isLlmEdited, setIsLlmEdited] = useState(false);
  const [isLlmCorrectionEdited, setIsLlmCorrectionEdited] = useState(false);
  const [pendingLlmChanges, setPendingLlmChanges] = useState<Record<string, string>>({});
  const [isTranscriptEdited, setIsTranscriptEdited] = useState(false);
  const [pendingTranscriptChange, setPendingTranscriptChange] = useState<string | null>(null);





  //Transkript-Content aktualisieren
  useEffect(() => {
    if (!isSiteSettingsLoading && logLevel >= 3) console.log("📡 sessionData wurde aktualisiert:", sessionData?.id);
    setTranscriptContent(sessionData?.data?.Transcript?.Text || 'Kein Transkript vorhanden'); // Transkript setzen
    setSelectedAudio(sessionData?.audio_file_name || '')
    setTranscriptModel(sessionData?.data?.Transcript?.Model || 'Unbekanntes Modell')
  }, [sessionData]);





  const handleSessionsUpdate = (updatedSessions: Number) => {
    if (!isSiteSettingsLoading && logLevel >= 3) console.log(isSessionSelected);
    setPrompt("")
    setPromptCorrection("")
    setIsSessionSelected(true);
  };



  useEffect(() => {
    if (sessionData && sessionData.data && sessionData.data.Documentation && sessionData.data.Documentation.length > 0) {
      const lastEntry = sessionData.data.Documentation[sessionData.data.Documentation.length - 1];

      setLlmResponse(lastEntry.llmResponse || "");
      setLlmResponseCorrection(lastEntry.llmResponseCorrection || "");
    } else {
      setLlmResponse("");
      setLlmResponseCorrection("");
    }
  }, [sessionData]);


  // Typ für apiCall hinzugefügt
  const handleRequest = async (apiCall: ApiCallFunction) => {
    setIsLoading(true);
    try {
      await apiCall();
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Error during API call:', error);
    } finally {
      setIsLoading(false);
    }
  };


  // Download eines Audio-Files (refactored: use session id, remove all file name logic)
  const handleAudioDownload = async () => {
    setIsPreparingAudioDownload(true);
    try {
      if (!sessionData?.id) {
        alert('Keine Sitzung ausgewählt.');
        return;
      }
      try {
        const response = await axios.get(`/api/services/audio-download/${sessionData.id}`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;

        const disposition = response.headers['content-disposition'];
        let filename = `audio_session_${sessionData.id}.mp3`;

        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) {
            filename = match[1];
          }
        }

        // Erzwinge .mp3-Endung (Browser-Kompatibilität)
        if (!filename.toLowerCase().endsWith('.mp3')) {
          filename = filename.replace(/\.[^/.]+$/, '') + '.mp3';
        }

        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        alert('Download erfolgreich.');
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Herunterladen der Audiodatei:', error);
        alert('Fehler beim Download. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setIsPreparingAudioDownload(false);
    }
  };


  const updateTranscriptContent = (newValue: string) => {
    setTranscriptContent(newValue);
    setPendingTranscriptChange(newValue);
    setIsTranscriptEdited(true); // 🟡 Bearbeitet-Status setzen
  };

  const saveTranscript = async () => {
    if (!sessionData?.id || !pendingTranscriptChange) return;

    try {
      const existingData = sessionData?.data || {};

      const updatedData = {
        ...existingData,
        Transcript: {
          ...existingData.Transcript,
          Text: pendingTranscriptChange,
        },
      };

      await axios.put("/api/apps/update-session/", {
        id: sessionData.id,
        data: updatedData,
      });

      if (!isSiteSettingsLoading && logLevel >= 3) console.log("✅ Transkript automatisch gespeichert.");
      setIsTranscriptEdited(false); // 🟢 Synchronisiert-Status setzen
      setPendingTranscriptChange(null);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim Speichern des Transkripts:", error);
    }
  };

  // Transcribe selected audio file
  const handleTranscriptionWhisper = async () => {

    try {
      setTranscriptContent('');
      const formData = new FormData();
      formData.append('session_id', String(sessionData?.id || ''));
      formData.append('filename', String(sessionData?.audio_file_name || ''));

      await handleRequest(async () => {
        await axios.post('/api/services/transcribe-audio-whisper/', formData);

      });

    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler bei der Transkription:', error);
      alert('Fehler bei der Transkription. Bitte versuchen Sie es erneut.');
    } finally {
      // Hier könnte man z.B. ein UI-Update oder Logging einfügen
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("Transkriptionsprozess beendet.");
    }
  };

  const handleTranscriptionWhisperDiarized = async () => {
    try {
      setTranscriptContent('');
      const formData = new FormData();
      formData.append('session_id', String(sessionData?.id || ''));
      formData.append('filename', String(sessionData?.audio_file_name || ''));

      await handleRequest(async () => {
        await axios.post('/api/services/transcribe-audio-whisper-diarized/', formData);
      });
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler bei der Diarisierung:', error);
      alert('Fehler bei der Diarisierung. Bitte versuchen Sie es erneut.');
    } finally {
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("Diarisierungsprozess beendet.");
    }
  };

  const saveLlmResponse = async () => {
    if (!sessionData?.id) return;
    const responseText = pendingLlmChanges["llmResponse"] ?? llmResponse;
    if (!responseText) return;

    try {
      const existingData = sessionData?.data || {};
      let updatedDocumentation = [...(existingData.Documentation || [])];

      if (updatedDocumentation.length > 0) {
        updatedDocumentation[updatedDocumentation.length - 1] = {
          ...updatedDocumentation[updatedDocumentation.length - 1],
          llmResponse: responseText,
        };
      } else {
        updatedDocumentation.push({ llmResponse: pendingLlmChanges["llmResponse"] });
      }

      const updatedData = {
        ...existingData,
        Documentation: updatedDocumentation,
      };

      await axios.put("/api/apps/update-session/", {
        id: sessionData.id,
        data: updatedData,
      });

      if (!isSiteSettingsLoading && logLevel >= 3) console.log("✅ LLM-Antwort automatisch gespeichert.");
      setIsLlmEdited(false); // Wolke auf "Synchronisiert" setzen 🟢
      setPendingLlmChanges(prev => {
        const { llmResponse, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim Speichern der LLM-Antwort:", error);
    }
  };

  const saveLlmResponseCorrection = async () => {
    if (!sessionData?.id) return;
    const responseText = pendingLlmChanges["llmResponseCorrection"] ?? llmResponseCorrection;
    if (!responseText) return;

    try {
      const existingData = sessionData?.data || {};
      let updatedDocumentation = [...(existingData.Documentation || [])];

      if (updatedDocumentation.length > 0) {
        updatedDocumentation[updatedDocumentation.length - 1] = {
          ...updatedDocumentation[updatedDocumentation.length - 1],
          llmResponseCorrection: responseText,
        };
      } else {
        updatedDocumentation.push({ llmResponseCorrection: pendingLlmChanges["llmResponseCorrection"] });
      }

      const updatedData = {
        ...existingData,
        Documentation: updatedDocumentation,
      };

      await axios.put("/api/apps/update-session/", {
        id: sessionData.id,
        data: updatedData,
      });

      if (!isSiteSettingsLoading && logLevel >= 3) console.log("✅ Korrigierte LLM-Antwort automatisch gespeichert.");
      setIsLlmCorrectionEdited(false); // Wolke auf "Synchronisiert" setzen 🟢
      setPendingLlmChanges(prev => {
        const { llmResponseCorrection, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim Speichern der korrigierten LLM-Antwort:", error);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (pendingLlmChanges["llmResponse"]) saveLlmResponse();
      if (pendingLlmChanges["llmResponseCorrection"]) saveLlmResponseCorrection();
    }, 3000); // 3 Sekunden warten

    return () => clearTimeout(timeout);
  }, [pendingLlmChanges]);

  const updateLlmResponse = (newValue: string) => {
    setLlmResponse(newValue);
    setPendingLlmChanges(prev => ({ ...prev, llmResponse: newValue }));
    setIsLlmEdited(true); // Wolken-Status auf "Bearbeitet" setzen 🟡
  };

  const updateLlmResponseCorrection = (newValue: string) => {
    setLlmResponseCorrection(newValue);
    setPendingLlmChanges(prev => ({ ...prev, llmResponseCorrection: newValue }));
    setIsLlmCorrectionEdited(true); // Wolken-Status auf "Bearbeitet" setzen 🟡
  };



  // Offene LLM Anfrage Ionos 
  const handleLlmRequestIonos = async () => {
    setLlmResponse('');
    setSelectedLlm(modelLlm);
    setDialogIonosOpen(false);

    await handleRequest(async () => {
      const response = await axios.post('/api/services/send-to-llm-ionos/', {
        transcript: transcriptContent,
        prompt: myprompt,
        model: modelLlm,
      });

      const generatedResponse = response.data.response;
      setLlmResponse(generatedResponse);

      // Speichern direkt nach der Generierung
      updateLlmResponse(generatedResponse);
      //saveLlmResponse();
    });
  };
  const handleLlmRequestIonosCorrection = async () => {
    setLlmResponseCorrection('');
    setDialogIonosCorrectionOpen(false);

    await handleRequest(async () => {
      const response = await axios.post('/api/services/send-to-llm-ionos/', {
        transcript: llmResponse,
        prompt: mypromptCorrection,
        model: modelLlmCorrection,
      });

      const correctedResponse = response.data.response;
      setLlmResponseCorrection(correctedResponse);

      // Speichern direkt nach der Generierung
      updateLlmResponseCorrection(correctedResponse);
      //saveLlmResponseCorrection();
    });
  };

  const handleLlmRequestLocal = async () => {
    setLlmResponse('');
    setSelectedLlm(modelLlm); // Setzt das ausgewählte Modell als ausgewählte LLM
    setDialogLlmOpen(false); // Schließe den Dialog nach Bestätigung
    await handleRequest(async () => {
      const response = await axios.post('/api/apps/send-to-llm-local/', {
        transcript: transcriptContent,
        prompt: myprompt,
        model: modelLlm, // Ausgewähltes Modell wird an die API gesendet
      });
      setLlmResponse(response.data.response);
    });
  };


  // Download combined result
  const handleDownload = () => {
    const currentDateTime = new Date().toLocaleString('de-DE', {
      dateStyle: 'long',
      timeStyle: 'short',
    }).replace(/[.,:]/g, '').replace(/ /g, '_'); // Formatierung des Datums und der Uhrzeit
    const fileName = `KVITA_${currentDateTime}.txt`;
    const blob = new Blob(
      [`Datum und Uhrzeit: ${currentDateTime}\n` +
        `Transkriptions-Model: ${transcriptModel || 'Keines ausgewählt'}\n` +
        `LLM: ${selectedLlm || 'Keine ausgewählt'}\n\n` +
        `------------ Transcript ------------\n\n` +
        `${transcriptContent}\n\n` +
        `------------ Prompt ------------\n\n` +
        `${myprompt}\n\n` +
        `------------ LLM Response ------------\n\n` +
        `${llmResponse}` +
        `------------ LLM Response (Correction)------------\n\n` +
        `${llmResponseCorrection}`


      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    header: {
      backgroundColor: '#2A5941',
      color: '#FFFFFF',
      padding: '20px',
      textAlign: 'center',
      borderRadius: '10px',
    },
    container: {
      backgroundColor: '#F1F8E9',
      padding: '20px',
      minHeight: '100vh',
    },
    button: {
      backgroundColor: '#C5E1A5',
      color: '#2A5941',
      margin: '10px',
      border: '1px solid #2A5941',
      borderRadius: '8px',
      padding: '10px 20px',
    },
    textField: {
      border: '1px solid #C5E1A5',
      borderRadius: '5px',
      marginTop: '10px',
      backgroundColor: '#FFFFFF',

    },

    dialogContent: {
      padding: '20px',
    },
  };






  ///////////////////////////////////
  // Anfang des HTML-Layouts ////////
  ///////////////////////////////////

  return (









<div style={{
  paddingLeft: '0px',
  maxHeight: '80vh',
  overflowY: 'auto',
  maxWidth: '1800px',
  margin: '0 auto'
}}>      <SessionDetailsDev />




      <div style={{ marginTop: '20px' }}>

      
        <Typography variant="h6" style={{ marginBottom: '10px', marginTop: '0px' }}>
          -------------------------  ENTWICKLUNGS  TOOLS ----------------------------
        </Typography>
        
        {/* Transcribe Button Whisper*/}
        <Button
          variant="contained"
          color="primary"
          onClick={handleTranscriptionWhisper}
          disabled={!selectedAudio}
          style={{ marginTop: '20px', marginRight: '10px' }}
        >
          Audiodatei mit Whisper transkribieren
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleTranscriptionWhisperDiarized}
          disabled={!selectedAudio}
          style={{ marginTop: '20px', marginRight: '10px' }}
        >
          Transkription mit Diarisierung
        </Button>

        {/* Transcribe Button Local Small */}

      </div>


      {/* Transcript Text Area */}
      <Typography variant="h6" style={{ marginTop: '20px' }}>
        Aktuelle Audiodatei: {selectedAudio || 'Keine Audio ausgewählt'}
        {selectedAudio && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAudioDownload}
              disabled={isPreparingAudioDownload}
              style={{ marginLeft: '10px' }}
            >
              {isPreparingAudioDownload ? (
                <>
                  <CircularProgress size={18} style={{ marginRight: '8px', color: 'white' }} />
                  Audiodatei wird vorbereitet…
                </>
              ) : (
                'Herunterladen'
              )}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                const confirmDelete = window.confirm('Soll die Audiodatei wirklich gelöscht werden?');
                if (!confirmDelete) return;
                try {
                  await axios.delete(`/api/apps/delete-audio/${sessionData!.id}/`);
                  alert('Audiodatei erfolgreich gelöscht.');
                } catch (error) {
                  if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Löschen der Audiodatei:', error);
                  alert('Fehler beim Löschen der Audiodatei.');
                }
              }}
              style={{ marginLeft: '10px' }}
            >
            🗑️
            </Button>
          </>
        )}
      </Typography>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>

        <Typography variant="h6" style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          Transkript (Editierbar)
          {isTranscriptEdited ? (
            <CloudSyncIcon sx={{ color: "gold", fontSize: 24 }} /> // 🟡 Bearbeitet
          ) : (
            <CloudDoneIcon sx={{ color: "green", fontSize: 24 }} /> // 🟢 Synchronisiert
          )}
        </Typography> <Button
          variant="contained"
          color="primary"
          onClick={saveTranscript}
          style={{ marginTop: '20px', marginLeft: '10px' }}
        >
          speichern
        </Button>
      </div>

      <TextField
        
        multiline
        minRows={15} // Mindesthöhe
        variant="outlined"
        value={transcriptContent}
        onChange={(e) => updateTranscriptContent(e.target.value)}
        sx={{
          width: '95%',
          marginTop: '10px',
          '& .MuiInputBase-root': {
            display: 'flex',
            flexDirection: 'column',
            resize: 'vertical', // Hier wird das Resizing aktiviert
            overflow: 'hidden', // Scrollbalken falls nötig
            height: '300px', // Feste Anfangshöhe setzen
          },
          '& textarea': {
            height: '100%', // Sicherstellen, dass das `textarea`-Element die volle Höhe nutzt
            overflow: 'auto', // Hier den Scrollbalken aktivieren!
          }

        }}
      />

      {/* Prompt Text Field */}
      <Typography variant="h6" style={{ marginTop: '20px' }}>
        Prompt für LLM
      </Typography>
      <PromptButtons
        setPrompt={setPrompt}
        myprompt={myprompt}
      />

      <TextField
        
        multiline
        minRows={10} // Mindesthöhe
        variant="outlined"
        value={myprompt}
        onChange={(e) => setPrompt(e.target.value)}
        sx={{
          width: '95%',
          marginTop: '10px',
          '& .MuiInputBase-root': {
            display: 'flex',
            flexDirection: 'column',
            resize: 'vertical', // Hier wird das Resizing aktiviert
            overflow: 'hidden', // Scrollbalken falls nötig
            height: '300px', // Feste Anfangshöhe setzen
          },
          '& textarea': {
            height: '100%', // Sicherstellen, dass das `textarea`-Element die volle Höhe nutzt
            overflow: 'auto', // Hier den Scrollbalken aktivieren!
          }

        }}
      />


      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>

        <FormControl style={{ minWidth: 200 }}>

          <InputLabel id="ionos-model-select-label">LLM-Modell</InputLabel>
          <Select
            labelId="ionos-model-select-label"
            value={modelLlm}
            onChange={(e) => setModelLlm(e.target.value)}
          >
            <MenuItem value="meta-llama/Llama-3.3-70B-Instruct">Llama-3.3-70B-Instruct</MenuItem>
            <MenuItem value="openai/gpt-oss-120b">gpt-oss-120b</MenuItem>


            <MenuItem value="mistralai/Mistral-7B-Instruct-v0.3">Mistral-7B-Instruct-v0.3</MenuItem>
            <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct">Meta-Llama-3.1-8B-Instruct</MenuItem>
            <MenuItem value="mistralai/Mixtral-8x7B-Instruct-v0.1">Mixtral-8x7B-Instruct-v0.1</MenuItem>
          </Select>
        </FormControl>
        {/* Send to LLM Button IONOS*/}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleLlmRequestIonos}
        >
          An LLM senden <br /> IONOS
        </Button>

      </div>

      {/* LLM Response */}
      <Typography variant="h6" style={{ marginTop: '20px' }}>
        Aktuelle LLM: {selectedLlm || 'Keine LLM ausgewählt'}
      </Typography>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px" }}>
          <Typography variant="h6">LLM Antwort</Typography>
          {isLlmEdited ? (
            <CloudSyncIcon sx={{ color: "gold", fontSize: 24 }} /> // 🟡 Bearbeitet
          ) : (
            <CloudDoneIcon sx={{ color: "green", fontSize: 24 }} /> // 🟢 Synchronisiert
          )}
        </Box><Button
          variant="contained"
          color="primary"
          onClick={saveLlmResponse}
          style={{ marginTop: '20px', marginLeft: '10px' }}
        >
          speichern
        </Button>
      </div>
      <TextField
        multiline
        minRows={10} // Mindesthöhe
        maxRows={Infinity} // Automatische Anpassung nach oben
       
        variant="outlined"
        value={llmResponse}
        onChange={(e) => updateLlmResponse(e.target.value)}
        sx={{ width: '95%' }} 


        style={{ marginTop: '10px' }}

      />
      {/* Zweites Prompt-Feld */}
      <Typography variant="h6" style={{ marginTop: '150px' }}>
        Zweiter Prompt für die Überarbeitung der LLM Antwort (Korrektur)
      </Typography>
      <PromptButtons2
        setPromptCorrection={setPromptCorrection}
        mypromptCorrection={mypromptCorrection}
      />
      <TextField
        
        multiline
        minRows={10} // Mindesthöhe
        variant="outlined"
        value={mypromptCorrection}
        onChange={(e) => setPromptCorrection(e.target.value)}
        sx={{
          width: '95%',
          marginTop: '10px',
          '& .MuiInputBase-root': {
            display: 'flex',
            flexDirection: 'column',
            resize: 'vertical', // Hier wird das Resizing aktiviert
            overflow: 'hidden', // Scrollbalken falls nötig
            height: '300px', // Feste Anfangshöhe setzen
          },
          '& textarea': {
            height: '100%', // Sicherstellen, dass das `textarea`-Element die volle Höhe nutzt
            overflow: 'auto', // Hier den Scrollbalken aktivieren!

          }

        }}
      />

      {/* Knopf für die zweite LLM-Anfrage */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>

        <FormControl style={{ minWidth: 200 }}>
          <InputLabel id="ionos-model-select-label">LLM-Modell</InputLabel>
          <Select
            labelId="ionos-model-select-label"
            value={modelLlm}
            onChange={(e) => setModelLlm(e.target.value)}
          >
            <MenuItem value="meta-llama/Llama-3.3-70B-Instruct">Llama-3.3-70B-Instruct</MenuItem>
                        <MenuItem value="openai/gpt-oss-120b">gpt-oss-120b</MenuItem>


            <MenuItem value="mistralai/Mistral-7B-Instruct-v0.3">Mistral-7B-Instruct-v0.3</MenuItem>
            <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct">Meta-Llama-3.1-8B-Instruct</MenuItem>
            <MenuItem value="mistralai/Mixtral-8x7B-Instruct-v0.1">Mixtral-8x7B-Instruct-v0.1</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleLlmRequestIonosCorrection}
        >
          An LLM senden <br />  (Korrektur)
        </Button>
      </div>
      {/* Dialog für Modellwahl der zweiten Anfrage */}
      <Dialog open={dialogIonosCorrectionOpen} onClose={() => setDialogIonosCorrectionOpen(false)}>
        <DialogTitle>Modell auswählen (Korrektur)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: '20px' }}>
            <InputLabel id="ionos-model-correction-select-label">Modell</InputLabel>
            <Select
              labelId="ionos-model-correction-select-label"
              value={modelLlmCorrection}
              onChange={(e) => setModelLlmCorrection(e.target.value)}
            >
              <MenuItem value="meta-llama/Llama-3.3-70B-Instruct">Llama-3.3-70B-Instruct</MenuItem>
                          <MenuItem value="openai/gpt-oss-120b">gpt-oss-120b</MenuItem>

              <MenuItem value="mistralai/Mistral-7B-Instruct-v0.3">Mistral-7B-Instruct-v0.3</MenuItem>
              <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct">Meta-Llama-3.1-8B-Instruct</MenuItem>
              <MenuItem value="mistralai/Mixtral-8x7B-Instruct-v0.1">Mixtral-8x7B-Instruct-v0.1</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogIonosCorrectionOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleLlmRequestIonosCorrection} color="secondary">
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Anzeige der zweiten LLM-Antwort */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px" }}>
          <Typography variant="h6">LLM Antwort (nach Korrektur)</Typography>
          {isLlmCorrectionEdited ? (
            <CloudSyncIcon sx={{ color: "gold", fontSize: 24 }} /> // 🟡 Bearbeitet
          ) : (
            <CloudDoneIcon sx={{ color: "green", fontSize: 24 }} /> // 🟢 Synchronisiert
          )}
        </Box><Button
          variant="contained"
          color="primary"
          onClick={saveLlmResponseCorrection}
          style={{ marginTop: '20px', marginLeft: '10px' }}
        >
          speichern
        </Button>
      </div>
      <TextField
        
        multiline
        minRows={10}
        variant="outlined"
        value={llmResponseCorrection}
        onChange={(e) => updateLlmResponseCorrection(e.target.value)}
        sx={{ width: '95%' }} 
      />


      {/* Download Result */}
      <Button variant="contained" color="success" onClick={handleDownload} style={{ marginTop: '20px' }}>
        Ergebnis herunterladen
      </Button>
      <Dialog open={dialogIonosOpen} onClose={() => setDialogIonosOpen(false)}>
        <DialogTitle>Modell auswählen (IONOS)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: '20px' }}>
            <InputLabel id="ionos-model-select-label">Modell</InputLabel>
            <Select
              labelId="ionos-model-select-label"
              value={modelLlm}
              onChange={(e) => setModelLlm(e.target.value)}
            >
              <MenuItem value="meta-llama/Llama-3.3-70B-Instruct">Llama-3.3-70B-Instruct</MenuItem>
                          <MenuItem value="openai/gpt-oss-120b">gpt-oss-120b</MenuItem>


              <MenuItem value="mistralai/Mistral-7B-Instruct-v0.3">Mistral-7B-Instruct-v0.3</MenuItem>
              <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct">Meta-Llama-3.1-8B-Instruct</MenuItem>
              <MenuItem value="mistralai/Mixtral-8x7B-Instruct-v0.1">Mixtral-8x7B-Instruct-v0.1</MenuItem>





            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogIonosOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleLlmRequestIonos} color="secondary">
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogLlmOpen} onClose={() => setDialogLlmOpen(false)}>
        <DialogTitle>Modell auswählen</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: '20px' }}>
            <InputLabel id="model-select-label">Modell</InputLabel>
            <Select
              labelId="model-select-label"
              value={modelLlm}
              onChange={(e) => setModelLlm(e.target.value)}
            >
              <MenuItem value="llama3.1">llama3.1</MenuItem>
              <MenuItem value="gemma2">gemma2</MenuItem>
              <MenuItem value="mistral">mistral</MenuItem>
              <MenuItem value="phi3:medium">phi3:medium</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogLlmOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleLlmRequestLocal} color="secondary">
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isLoading} onClose={closeLoadingDialog}>
        <DialogContent>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress style={{ marginRight: '10px' }} />
            <Typography>Bitte warten...</Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLoadingDialog}>Ausblenden <br />  (bricht nicht ab!)</Button>
        </DialogActions>
      </Dialog>

      {/* <AudioRecorder
                    isAudioDialogOpen={isAudioDialogOpen}
                    setIsAudioDialogOpen={setIsAudioDialogOpen}
                  />*/}


    </div>


  );
}

export default DevTools;
